import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import type { CodeScannerStandard, CodeScannerStandards} from "common/code-scanner-standards";
import { isEnabledCodeScannerStandard } from "common/code-scanner-standards";
import { asArrayValue, asBoolean, asString, isLoadedDefined } from "common/components/component-helpers";
import { LOADING } from "common/components/computation-types";
import type { Context, PotentialValue, RenderEnvironment, ValueProducer } from "common/components/types";
import { ActionSource } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import * as React from "react";
import CodeScanner from "webapp/ui/smart/code-scanner/code-scanner";

// The Code Scanner is _extremely_ sensitive to re-renders, especially
// ones that trash the DOM state. We rely on various bits of state tracking
// both in DOM nodes and in React state to properly capture files you
// "upload".
//
// useCallback, useMemo, and friends are all natural solutions to this problem.
// But React goes out of its way to refuse those to us unless we use them in
// exactly a Function component. Methods are apparently not good enough. So we
// have to stutter this whole implementation just to solve a problem React
// manufactured for itself.

type CodeScannerMaterialProps = Readonly<{
    standardsPV: PotentialValue;
    onSuccessPV: PotentialValue;
    acceptOnFirst: boolean;
    outputVP: ValueProducer;
    ctx: Context;
    env: RenderEnvironment;
}>;

function CodeScannerMaterial({
    standardsPV,
    onSuccessPV,
    acceptOnFirst,
    outputVP,
    ctx,
    env,
}: CodeScannerMaterialProps): JSX.Element {
    const standards = React.useMemo(
        () => asArrayValue(standardsPV).map(v => asString(v.current)) as CodeScannerStandards,
        [standardsPV]
    );
    const { onClick: onSuccess } = useOnClickFromAction(onSuccessPV, ActionSource.None, ctx, env);

    const handleValue = React.useCallback(
        (standard: CodeScannerStandard, value: string) => {
            if (!isEnabledCodeScannerStandard(standards, standard)) return;
            outputVP.set(value);
            // FIXME: We should have been able to thread through the ActionSource here.
            const f = onSuccess?.(undefined);
            definedMap(f, dontAwait);
        },
        [standards, outputVP, onSuccess]
    );

    return (
        <CodeScanner
            standards={standards}
            acceptOnFirst={acceptOnFirst}
            onValue={handleValue}
            appKind={env.appEnvironment.appKind}
        />
    );
}
export const CodeScannerMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[standardsPV, acceptOnFirstPV, , onSuccessPV], [, , outputVP]] = useForceValueProducers(
        getValueProducers,
        force,
        ctx
    );

    if (
        !isLoadedDefined(standardsPV) ||
        onSuccessPV === LOADING ||
        !isLoadedDefined(outputVP) ||
        !isLoadedDefined(acceptOnFirstPV)
    ) {
        return null;
    }

    return (
        <CodeScannerMaterial
            standardsPV={standardsPV}
            onSuccessPV={onSuccessPV}
            acceptOnFirst={asBoolean(acceptOnFirstPV)}
            outputVP={outputVP}
            ctx={ctx}
            env={env}
        />
    );
};
